import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  Briefcase, Users, Clock, CheckCircle2, Plus, Pencil, Trash2, Power,
  Loader2, Eye, FileDown, Building2, X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import StatusBadge from "../components/StatusBadge";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const TYPES = ["full_time", "part_time", "contract", "internship", "freelance", "remote"];
const CATEGORIES = ["tech", "marketing", "sales", "design", "finance", "hr", "health", "education", "other"];
const STATUSES = ["pending", "reviewed", "interview", "rejected", "hired"];
const EMPTY_JOB = { title: "", description: "", location: "", job_type: "full_time", category: "tech", salary: "", requirements: "", min_experience: "", questions: [] };
const EXP_OPTIONS = ["lt1", "1-3", "3-5", "5+"];

const inputClass = "w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";
const labelClass = "text-xs font-bold uppercase tracking-wider text-slate-600";

function KpiCard({ icon: Icon, value, label, color, testid }) {
  return (
    <div className="rounded-md border-2 border-slate-900 bg-white p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[5px_5px_0px_0px_rgba(15,23,42,1)]" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-heading text-3xl font-bold text-slate-900">{value}</p>
          <p className="mt-1 text-sm text-slate-500">{label}</p>
        </div>
        <span className={`flex h-12 w-12 items-center justify-center rounded-md border-2 border-slate-900 ${color}`}>
          <Icon className="h-5 w-5 text-slate-900" strokeWidth={2} />
        </span>
      </div>
    </div>
  );
}

function JobFormModal({ initial, onClose, onSaved, t }) {
  const [form, setForm] = useState(initial || EMPTY_JOB);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.job_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/jobs/${initial.job_id}`, form);
        toast.success(t("company.jobUpdated"));
      } else {
        await api.post("/jobs", form);
        toast.success(t("company.jobCreated"));
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="job-form-modal">
      <div className="my-8 w-full max-w-2xl rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)]">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-slate-900">{isEdit ? t("company.editJob") : t("company.newJob")}</h2>
          <button onClick={onClose} data-testid="job-form-close" className="rounded-md border-2 border-slate-900 p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="job-form">
          <div className="sm:col-span-2">
            <label className={labelClass}>{t("company.jobTitle")}</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("company.jobTitlePlaceholder")} data-testid="job-form-title" className={`${inputClass} mt-1.5`} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>{t("company.description")}</label>
            <textarea required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("company.descriptionPlaceholder")} data-testid="job-form-description" className={`${inputClass} mt-1.5`} />
          </div>
          <div>
            <label className={labelClass}>{t("company.location")}</label>
            <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t("company.locationPlaceholder")} data-testid="job-form-location" className={`${inputClass} mt-1.5`} />
          </div>
          <div>
            <label className={labelClass}>{t("jobs.salary")}</label>
            <input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder={t("company.salaryPlaceholder")} data-testid="job-form-salary" className={`${inputClass} mt-1.5`} />
          </div>
          <div>
            <label className={labelClass}>{t("company.type")}</label>
            <select value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} data-testid="job-form-type" className={`${inputClass} mt-1.5`}>
              {TYPES.map((ty) => <option key={ty} value={ty}>{t(`jobTypes.${ty}`)}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t("jobs.category")}</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="job-form-category" className={`${inputClass} mt-1.5`}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>{t("company.requirements")}</label>
            <textarea rows={3} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder={t("company.requirementsPlaceholder")} data-testid="job-form-requirements" className={`${inputClass} mt-1.5`} />
          </div>
          <div>
            <label className={labelClass}>{t("company.minExperience")}</label>
            <select value={form.min_experience} onChange={(e) => setForm({ ...form, min_experience: e.target.value })} data-testid="job-form-min-experience" className={`${inputClass} mt-1.5`}>
              <option value="">{t("company.noRequirement")}</option>
              {EXP_OPTIONS.map((x) => <option key={x} value={x}>{t(`experience.${x}`)}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>{t("company.questions")}</label>
            {(form.questions || []).map((q, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input
                  value={q.text}
                  onChange={(e) => { const qs = [...form.questions]; qs[i] = { ...qs[i], text: e.target.value }; setForm({ ...form, questions: qs }); }}
                  placeholder={t("company.questionPlaceholder")}
                  data-testid={`job-form-question-${i}`}
                  className={inputClass}
                />
                <select
                  value={q.type || "text"}
                  onChange={(e) => { const qs = [...form.questions]; qs[i] = { ...qs[i], type: e.target.value }; setForm({ ...form, questions: qs }); }}
                  data-testid={`job-form-question-type-${i}`}
                  className="shrink-0 rounded-md border-2 border-slate-900 bg-white px-2 text-xs font-semibold"
                  title={t("company.questionType")}
                >
                  <option value="text">{t("company.typeText")}</option>
                  <option value="yesno">{t("company.typeYesNo")}</option>
                </select>
                <button type="button" onClick={() => setForm({ ...form, questions: form.questions.filter((_, j) => j !== i) })} data-testid={`job-form-question-remove-${i}`}
                  className="shrink-0 rounded-md border-2 border-slate-900 bg-red-100 px-3 transition-transform hover:-translate-y-0.5"><X className="h-4 w-4" /></button>
              </div>
            ))}
            {(form.questions || []).length < 5 && (
              <button type="button" onClick={() => setForm({ ...form, questions: [...(form.questions || []), { text: "", type: "text" }] })} data-testid="job-form-add-question"
                className="mt-2 rounded-full border-2 border-slate-900 bg-sky-100 px-4 py-1.5 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
                {t("company.addQuestion")}
              </button>
            )}
          </div>
          <div className="flex gap-3 sm:col-span-2">
            <button type="button" onClick={onClose} data-testid="job-form-cancel" className="flex-1 rounded-full border-2 border-slate-900 bg-white py-2.5 text-sm font-bold text-slate-900">{t("company.cancel")}</button>
            <button type="submit" disabled={saving} data-testid="job-form-submit" className="flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? t("company.save") : t("company.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CandidateModal({ application, onClose, t }) {
  const [candidate, setCandidate] = useState(null);
  useEffect(() => {
    api.get(`/company/candidates/${application.candidate_id}`).then(({ data }) => setCandidate(data)).catch(() => {});
  }, [application.candidate_id]);
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="candidate-profile-modal">
      <div className="w-full max-w-lg rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)]">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-slate-900">{t("company.candidateProfile")}</h2>
          <button onClick={onClose} data-testid="candidate-modal-close" className="rounded-md border-2 border-slate-900 p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 space-y-3 text-sm">
          <p className="font-heading text-lg font-bold text-slate-900">{application.candidate_name}</p>
          <p className="text-slate-600">{application.candidate_email}</p>
          {candidate?.title && <p><span className="font-semibold">{t("candidate.jobTitleLabel")}:</span> {candidate.title}</p>}
          {candidate?.location && <p><span className="font-semibold">{t("common.location")}:</span> {candidate.location}</p>}
          {candidate?.skills && <p><span className="font-semibold">{t("candidate.skills")}:</span> {candidate.skills}</p>}
          {candidate?.experience && <p><span className="font-semibold">{t("candidate.experience")}:</span> {candidate.experience}</p>}
          {candidate?.bio && <p className="rounded-md bg-slate-50 p-3 text-slate-600">{candidate.bio}</p>}
          {application.experience_years && (
            <p><span className="font-semibold">{t("company.declaredExperience")}:</span> {t(`experience.${application.experience_years}`)}</p>
          )}
          {application.birthdate && (
            <p><span className="font-semibold">{t("jobs.birthdate")}:</span> {application.birthdate}</p>
          )}
          {application.qualifications && (
            <div>
              <p className="font-semibold">{t("jobs.requirementsLabel")}:</p>
              <p className="mt-1 whitespace-pre-line rounded-md bg-slate-50 p-3 text-slate-600">{application.qualifications}</p>
            </div>
          )}
          {application.experience_detail && (
            <div>
              <p className="font-semibold">{t("jobs.proExperience")}:</p>
              <p className="mt-1 whitespace-pre-line rounded-md bg-slate-50 p-3 text-slate-600">{application.experience_detail}</p>
            </div>
          )}
          {(application.qa || []).length > 0 && (
            <div>
              <p className="font-semibold">{t("company.answers")}:</p>
              <div className="mt-1 space-y-2">
                {application.qa.map((qa, i) => (
                  <div key={i} className="rounded-md bg-slate-50 p-3" data-testid={`qa-${i}`}>
                    <p className="text-xs font-bold text-slate-700">{qa.question}</p>
                    <p className="mt-1 text-slate-600">{qa.type === "yesno" ? (qa.answer === "yes" ? t("common.yes") : t("common.no")) : (qa.answer || "—")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {application.message && (
            <div>
              <p className="font-semibold">{t("company.message")}:</p>
              <p className="mt-1 rounded-md bg-sky-50 p-3 text-slate-700">{application.message}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 pt-2">
            {application.cv_path ? (
              <a href={`${apiBase}/files/${application.cv_path}`} target="_blank" rel="noreferrer" data-testid="download-cv-link"
                className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-4 py-2 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
                <FileDown className="h-4 w-4" strokeWidth={2} /> {t("company.downloadCv")}
              </a>
            ) : <span className="text-xs text-slate-400">{t("company.noCv")}</span>}
            {application.cover_letter_path && (
              <a href={`${apiBase}/files/${application.cover_letter_path}`} target="_blank" rel="noreferrer" data-testid="download-cover-letter-link"
                className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-yellow-300 px-4 py-2 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
                <FileDown className="h-4 w-4" strokeWidth={2} /> {t("company.downloadCoverLetter")}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CompanyDashboard() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [viewingApp, setViewingApp] = useState(null);
  const [filterJob, setFilterJob] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [profile, setProfile] = useState({ name: "", company_name: "", company_description: "", company_website: "", company_location: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";

  const fetchJobs = useCallback(async () => {
    const { data } = await api.get("/company/jobs");
    setJobs(data);
  }, []);

  const fetchApplications = useCallback(async () => {
    const { data } = await api.get("/company/applications", { params: { job_id: filterJob, status: filterStatus } });
    setApplications(data);
  }, [filterJob, filterStatus]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchJobs().catch(() => {});
      setLoading(false);
    })();
  }, [fetchJobs]);

  useEffect(() => { fetchApplications().catch(() => {}); }, [fetchApplications]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "", company_name: user.company_name || "",
        company_description: user.company_description || "", company_website: user.company_website || "",
        company_location: user.company_location || "",
      });
    }
  }, [user]);

  const toggleJobStatus = async (job) => {
    const next = job.status === "active" ? "inactive" : "active";
    await api.patch(`/jobs/${job.job_id}/status`, { status: next });
    toast.success(t("company.statusUpdated"));
    fetchJobs();
  };

  const deleteJob = async (job) => {
    if (!window.confirm(t("company.deleteConfirm"))) return;
    await api.delete(`/jobs/${job.job_id}`);
    toast.success(t("company.jobDeleted"));
    fetchJobs();
    fetchApplications();
  };

  const changeAppStatus = async (app, status) => {
    await api.patch(`/applications/${app.application_id}/status`, { status });
    toast.success(t("company.statusUpdated"));
    fetchApplications();
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/profile", profile);
      toast.success(t("company.profileSaved"));
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    } finally {
      setSavingProfile(false);
    }
  };

  const TABS = [
    { key: "overview", label: t("company.overview"), icon: Briefcase },
    { key: "jobs", label: t("company.myJobs"), icon: Briefcase },
    { key: "applications", label: t("company.applications"), icon: Users },
    { key: "profile", label: t("company.profile"), icon: Building2 },
  ];

  const activeJobs = jobs.filter((j) => j.status === "active").length;
  const pendingApps = applications.filter((a) => a.status === "pending").length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-dashboard">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{user?.company_name || user?.name}</h1>
            <p className="mt-1 text-sm text-slate-500">{t("nav.dashboard")}</p>
          </div>
          <button
            onClick={() => { setEditingJob(null); setShowJobForm(true); }}
            data-testid="new-job-button"
            className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-5 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} /> {t("company.newJob")}
          </button>
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto border-b-2 border-slate-200" data-testid="company-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              data-testid={`company-tab-${tb.key}`}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === tb.key ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <tb.icon className="h-4 w-4" strokeWidth={2} /> {tb.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
        ) : (
          <div className="mt-8">
            {tab === "overview" && (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard icon={Briefcase} value={jobs.length} label={t("company.totalJobs")} color="bg-sky-100" testid="kpi-total-jobs" />
                <KpiCard icon={CheckCircle2} value={activeJobs} label={t("company.activeJobs")} color="bg-green-100" testid="kpi-active-jobs" />
                <KpiCard icon={Users} value={applications.length} label={t("company.totalApplications")} color="bg-yellow-100" testid="kpi-total-apps" />
                <KpiCard icon={Clock} value={pendingApps} label={t("company.pendingApplications")} color="bg-orange-100" testid="kpi-pending-apps" />
              </div>
            )}

            {tab === "jobs" && (
              <div>
                {jobs.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="no-jobs-message">{t("company.noJobs")}</div>
                ) : (
                  <div className="overflow-x-auto rounded-md border-2 border-slate-900 bg-white" data-testid="jobs-table">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b-2 border-slate-900 bg-sky-50">
                        <tr>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.jobTitle")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("jobs.category")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.location")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("status.active")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.totalApplications")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.map((job) => (
                          <tr key={job.job_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`job-row-${job.job_id}`}>
                            <td className="px-5 py-3.5 font-semibold text-slate-900">{job.title}</td>
                            <td className="px-5 py-3.5 text-slate-600">{t(`categories.${job.category}`)}</td>
                            <td className="px-5 py-3.5 text-slate-600">{job.location}</td>
                            <td className="px-5 py-3.5"><StatusBadge status={job.status} t={t} /></td>
                            <td className="px-5 py-3.5 text-slate-600">{job.applicants_count} {t("company.applicants")}</td>
                            <td className="px-5 py-3.5">
                              <div className="flex gap-2">
                                <button onClick={() => { setEditingJob(job); setShowJobForm(true); }} data-testid={`edit-job-${job.job_id}`} title={t("company.edit")}
                                  className="rounded-md border border-slate-900 bg-white p-1.5 transition-transform hover:-translate-y-0.5 hover:bg-sky-100"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => toggleJobStatus(job)} data-testid={`toggle-job-${job.job_id}`} title={job.status === "active" ? t("company.deactivate") : t("company.activate")}
                                  className={`rounded-md border border-slate-900 p-1.5 transition-transform hover:-translate-y-0.5 ${job.status === "active" ? "bg-yellow-100" : "bg-green-100"}`}><Power className="h-3.5 w-3.5" /></button>
                                <button onClick={() => deleteJob(job)} data-testid={`delete-job-${job.job_id}`} title={t("company.delete")}
                                  className="rounded-md border border-slate-900 bg-red-100 p-1.5 transition-transform hover:-translate-y-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === "applications" && (
              <div>
                <div className="mb-5 flex flex-wrap gap-3">
                  <select value={filterJob} onChange={(e) => setFilterJob(e.target.value)} data-testid="filter-applications-job" className="rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm">
                    <option value="">{t("company.allJobs")}</option>
                    {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
                  </select>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-applications-status" className="rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm">
                    <option value="">{t("company.allStatuses")}</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
                  </select>
                </div>
                {applications.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="no-applications-message">{t("company.noApplications")}</div>
                ) : (
                  <div className="overflow-x-auto rounded-md border-2 border-slate-900 bg-white" data-testid="applications-table">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b-2 border-slate-900 bg-sky-50">
                        <tr>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.candidate")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.job")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.date")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.changeStatus")}</th>
                          <th className="px-5 py-3 font-heading font-bold">{t("company.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map((a) => (
                          <tr key={a.application_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`application-row-${a.application_id}`}>
                            <td className="px-5 py-3.5">
                              <p className="font-semibold text-slate-900">{a.candidate_name}</p>
                              <p className="text-xs text-slate-500">{a.candidate_title || a.candidate_email}</p>
                            </td>
                            <td className="px-5 py-3.5 text-slate-600">{a.job_title}</td>
                            <td className="px-5 py-3.5 text-slate-600">{dayjs(a.created_at).locale(locale).format("DD/MM/YYYY")}</td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <StatusBadge status={a.status} t={t} />
                                <select
                                  value={a.status}
                                  onChange={(e) => changeAppStatus(a, e.target.value)}
                                  data-testid={`application-status-select-${a.application_id}`}
                                  className="rounded-md border border-slate-900 bg-white px-2 py-1 text-xs"
                                >
                                  {STATUSES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
                                </select>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <button onClick={() => setViewingApp(a)} data-testid={`view-candidate-${a.application_id}`} title={t("company.viewProfile")}
                                className="rounded-md border border-slate-900 bg-sky-100 p-1.5 transition-transform hover:-translate-y-0.5"><Eye className="h-3.5 w-3.5" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === "profile" && (
              <form onSubmit={saveProfile} className="max-w-2xl rounded-md border-2 border-slate-900 bg-white p-8" data-testid="company-profile-form">
                <div className="grid gap-4">
                  <div>
                    <label className={labelClass}>{t("company.companyName")}</label>
                    <input value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} data-testid="profile-company-name" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div>
                    <label className={labelClass}>{t("auth.name")}</label>
                    <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-contact-name" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div>
                    <label className={labelClass}>{t("company.companyDescLabel")}</label>
                    <textarea rows={4} value={profile.company_description} onChange={(e) => setProfile({ ...profile, company_description: e.target.value })} data-testid="profile-company-description" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>{t("company.website")}</label>
                      <input value={profile.company_website} onChange={(e) => setProfile({ ...profile, company_website: e.target.value })} data-testid="profile-company-website" className={`${inputClass} mt-1.5`} />
                    </div>
                    <div>
                      <label className={labelClass}>{t("company.companyLocation")}</label>
                      <input value={profile.company_location} onChange={(e) => setProfile({ ...profile, company_location: e.target.value })} data-testid="profile-company-location" className={`${inputClass} mt-1.5`} />
                    </div>
                  </div>
                  <button type="submit" disabled={savingProfile} data-testid="profile-save-button"
                    className="flex w-fit items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-6 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60">
                    {savingProfile && <Loader2 className="h-4 w-4 animate-spin" />} {t("company.save")}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {showJobForm && (
        <JobFormModal initial={editingJob} onClose={() => { setShowJobForm(false); setEditingJob(null); }} onSaved={fetchJobs} t={t} />
      )}
      {viewingApp && <CandidateModal application={viewingApp} onClose={() => setViewingApp(null)} t={t} />}
    </div>
  );
}
